import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon, Who } from './AdminShell';
import { Modal, Summary, Checklist, EmptyState } from './Overlay';
import { api, documentUploadDir } from '../../config';
import { text } from './peopleCopy';

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }), credentials: 'include'
}).then(r => r.json());

const Q = `{ getProviderApprovals {
  count awaitingUpload status errorMessage
  results { id name email phone services area appliedAt version resubmission
    documents { id type imageName createdAt } }
} }`;

const M = `mutation ($userId: String!, $action: String!, $documentIds: [String],
  $reason: String, $version: String) {
  reviewProviderDocuments(userId: $userId, action: $action, documentIds: $documentIds,
    reason: $reason, version: $version) { status errorMessage }
}`;

const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? '—' : d.toISOString().slice(0, 10);
};
const imageUrl = imageName => `${api.apiEndpoint}${documentUploadDir}medium_${imageName}`;
const localImageUrl = imageName => `${documentUploadDir}medium_${imageName}`;
const DOC_LABELS = {
  identity: ['Work license document', 'مستند ترخيص العمل'],
  experience: ['Professional certificates', 'الشهادات المهنية'],
  vehicle_registration: ['Vehicle registration', 'تسجيل المركبة'],
  driving_licence: ['Driving licence', 'رخصة القيادة'],
};
// Jatuh kembali ke nilai enum mentah, bukan string kosong: jenis dokumen baru
// yang belum diberi label tetap harus bisa ditinjau admin.
const label = (type, locale) => DOC_LABELS[type] ? text(locale, ...DOC_LABELS[type]) : type;
const REJECT_REASONS = ['Documents cannot be verified', 'Identity does not match',
  'Documents expired', 'Application details are incomplete', 'Other'];

const ApprovalDetails = ({ provider, locale }) => (
  <div className="approval-details">
    <div><span>{text(locale, 'Phone', 'الهاتف')}</span><b dir="ltr">{provider.phone || '—'}</b></div>
    <div><span>{text(locale, 'Applied', 'تاريخ التقديم')}</span><b>{day(provider.appliedAt)}</b></div>
    <div><span>{text(locale, 'Documents', 'المستندات')}</span><b>{provider.documents.length} {text(locale, 'waiting', 'معلقة')}</b></div>
  </div>
);

class ProviderApprovals extends React.Component {
  state = { rows: [], awaitingUpload: 0, loading: true, error: null,
    reviewing: null, document: null, documentSrc: null, action: null, selected: [],
    reason: '', saving: false, notice: null };

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  componentDidMount() { this.load(); }

  load = async () => {
    this.setState({ loading: true, error: null });
    const json = await gql(Q).catch(() => null);
    const res = json && json.data && json.data.getProviderApprovals;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || 'Could not load approvals.' });
      return;
    }
    this.setState({ rows: res.results || [], awaitingUpload: res.awaitingUpload || 0, loading: false });
  };

  open = (provider, action) => this.setState({
    reviewing: provider, action,
    selected: action === 'changes' ? provider.documents.map(d => d.id) : [],
    reason: '', notice: null
  });

  openDocument = document => this.setState({ document, documentSrc: imageUrl(document.imageName) });

  save = async () => {
    const { reviewing, action, selected, reason } = this.state;
    this.setState({ saving: true });
    const json = await gql(M, {
      userId: reviewing.id, action, documentIds: selected,
      reason: reason.trim() || undefined, version: reviewing.version
    }).catch(() => null);
    const res = json && json.data && json.data.reviewProviderDocuments;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || 'Could not save review.' } });
      return;
    }
    const verb = action === 'approve' ? 'approved' : action === 'changes' ? 'sent back for changes' : 'rejected';
    this.setState({ reviewing: null, action: null, notice: {
      tone: action === 'approve' ? 'amber' : 'danger',
      text: `${reviewing.name || reviewing.email} ${verb}.`
    } });
    this.load();
  };

  renderModal() {
    const { reviewing: p, action, selected, reason, saving } = this.state;
    if (!p) return null;
    if (action === 'approve') return (
      <Modal icon="shield" tone="ok" title={`${this.tx('Approve', 'الموافقة على')} ${p.name || p.email}?`}
        sub={this.tx('All pending documents will be approved and this provider can receive jobs.', 'ستتم الموافقة على جميع المستندات المعلقة ويمكن لمقدم الخدمة استقبال الطلبات.')}
        confirmLabel={this.tx('Approve provider', 'الموافقة على مقدم الخدمة')} busy={saving} onConfirm={this.save}
        onClose={() => this.setState({ reviewing: null })}>
        <Summary pairs={[
          [this.tx('Documents', 'المستندات'), `${p.documents.length} ${this.tx('pending', 'معلقة')}`],
          [this.tx('Services', 'الخدمات'), p.services || this.tx('None set', 'غير محدد')], [this.tx('Area', 'المنطقة'), p.area || '—']
        ]} />
        <Checklist items={p.documents.map(d => ({ label: label(d.type), detail: day(d.createdAt) }))} />
      </Modal>
    );

    const changes = action === 'changes';
    return (
      <Modal icon="x" tone="danger"
        title={changes ? `${this.tx('Request changes from', 'طلب تغييرات من')} ${p.name || p.email}` : `${this.tx('Reject', 'رفض')} ${p.name || p.email}?`}
        sub={changes ? this.tx('Select every document they need to replace.', 'حدد كل مستند يحتاج إلى استبدال.') : this.tx('They remain pending and cannot receive jobs.', 'سيبقون قيد الانتظار ولن يتمكنوا من استقبال الطلبات.')}
        confirmLabel={changes ? this.tx('Request changes', 'طلب تغييرات') : this.tx('Reject application', 'رفض الطلب')} busy={saving}
        disabled={!reason.trim() || (changes && !selected.length)} onConfirm={this.save}
        onClose={() => this.setState({ reviewing: null })}>
        {changes && (
          <div className="field">
            <label>{this.tx('Documents to replace', 'المستندات المطلوب استبدالها')}</label>
            {p.documents.map(d => (
              <label key={d.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 400 }}>
                <input type="checkbox" checked={selected.indexOf(d.id) >= 0}
                  onChange={e => this.setState({ selected: e.target.checked
                    ? selected.concat(d.id) : selected.filter(id => id !== d.id) })} />
                {label(d.type, this.locale())}
              </label>
            ))}
          </div>
        )}
        <div className="field">
          <label>{changes ? this.tx('Note for provider', 'ملاحظة لمقدم الخدمة') : this.tx('Rejection reason', 'سبب الرفض')}</label>
          {changes
            ? <textarea className="inp" maxLength="160" value={reason}
                onChange={e => this.setState({ reason: e.target.value })} />
            : <select className="inp" value={reason} onChange={e => this.setState({ reason: e.target.value })}>
                <option value="">{this.tx('Choose a reason…', 'اختر سبباً…')}</option>
                {REJECT_REASONS.map(r => <option key={r}>{r}</option>)}
              </select>}
          <span className="hint">{this.tx('Required and saved in the audit log.', 'مطلوب ويتم حفظه في سجل التدقيق.')}</span>
        </div>
      </Modal>
    );
  }

  render() {
    const { rows, awaitingUpload, loading, error, reviewing, document, documentSrc, notice } = this.state;
    const pendingTotal = rows.length + awaitingUpload;
    return (
      <AdminShell current="approvals" breadcrumb={[this.tx('People', 'الأشخاص'), this.tx('Provider approvals', 'موافقات مقدمي الخدمة')]}
        title={this.tx('Provider approvals', 'موافقات مقدمي الخدمة')}
        lead={this.tx('Review uploaded documents. Providers without documents are counted separately below.', 'راجع المستندات المرفوعة. يتم احتساب مقدمي الخدمة دون مستندات بشكل منفصل أدناه.')}>
        {notice && <div className={`notice ${notice.tone}`}><span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
          <span className="nt">{notice.text}</span></div>}
        {!!awaitingUpload && <div className="notice info"><span className="ni"><Icon name="bell" /></span>
          <span className="nt"><b>{awaitingUpload} of {pendingTotal} pending providers</b> still need to upload documents.{' '}
            {rows.length} {rows.length === 1 ? 'is' : 'are'} ready for review below.</span>
          <a className="btn ghost sm" href="/admin/providers">{this.tx('Open providers', 'فتح مقدمي الخدمة')}</a></div>}
        {reviewing && this.renderModal()}
        {document && <Modal narrow showClose title={label(document.type, this.locale())} sub={this.tx('Provider document preview', 'معاينة مستند مقدم الخدمة')}
          cancelLabel={this.tx('Close', 'إغلاق')} hideConfirm onClose={() => this.setState({ document: null, documentSrc: null })}>
          <div className="document-preview">
            <img src={documentSrc} alt={label(document.type, this.locale())}
              onError={e => {
                const fallback = localImageUrl(document.imageName);
                if (e.currentTarget.src !== `${window.location.origin}${fallback}`) e.currentTarget.src = fallback;
              }} />
          </div>
        </Modal>}
        {error ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : loading ? <section className="card"><div className="card-b">{this.tx('Loading approvals…', 'جارٍ تحميل الموافقات…')}</div></section>
            : rows.length === 0 ? <section className="card"><EmptyState icon="shield"
                title={this.tx('Nothing waiting', 'لا يوجد شيء معلق')} msg={this.tx('Every uploaded document has a decision.', 'تم اتخاذ قرار بشأن كل المستندات المرفوعة.')} /></section>
              : <div className="grid g-2">{rows.map(p => (
                <section className="card" key={p.id}>
                  <div className="card-h"><Who name={p.name || 'Unnamed provider'} sub={p.id.slice(0, 8)} />
                    <div className="approval-tags">
                      {p.services && <span className="tag amber">{p.services.split(',')[0].trim()}</span>}
                      {p.area && <span className="tag">{p.area.split(',')[0].trim()}</span>}
                    </div>
                    <span className="sp"><span className={`tag ${p.resubmission ? 'amber' : 'warn'}`}>
                      <i />{p.resubmission ? this.tx('Re-submitted', 'أُعيد التقديم') : this.tx('Pending', 'قيد الانتظار')}
                    </span></span></div>
                  <div className="card-b">
                    <ApprovalDetails provider={p} locale={this.locale()} />
                    <div className="approval-docs">
                      {p.documents.map(d => <button className="tag warn" key={d.id} type="button"
                        onClick={() => this.openDocument(d)}>
                        <Icon name="eye" />{label(d.type, this.locale())}
                      </button>)}
                    </div>
                    <div className="approval-actions">
                      <button className="btn" type="button" onClick={() => this.open(p, 'approve')}><Icon name="check" />{this.tx('Approve', 'موافقة')}</button>
                      <button className="btn ghost" type="button" onClick={() => this.open(p, 'changes')}>{this.tx('Request changes', 'طلب تغييرات')}</button>
                      <button className="btn ghost" type="button" onClick={() => this.open(p, 'reject')}>{this.tx('Reject', 'رفض')}</button>
                    </div>
                  </div>
                </section>
              ))}</div>}
      </AdminShell>
    );
  }
}

export default injectIntl(ProviderApprovals);
